import { createContext, ReactElement, ReactNode, useCallback, useContext, useEffect, useMemo, useRef } from 'react'

import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { useGetZervantDocumentSettings } from '@features/invoicingSettings/hooks/useGetZervantDocumentSettings'

import { UseFormReturn } from '../../../../../hooks'
import { useUpdateInvoiceColumnsVisibilitySettings } from '../hooks/useUpdateInvoiceColumnsVisibilitySettings'
import { InvoiceColumnVisibilityForm } from '../types/invoiceColumnVisibilityForm/invoiceColumnVisibilityForm'
import { InvoiceColumnVisibilityFormSchema } from '../types/invoiceColumnVisibilityForm/invoiceColumnVisibilityFormSchema'
import { getInvoiceColumnVisibilityFormDefaultValues } from '../utils/invoiceLinesColumnsVisibilityForm/getInvoiceColumnVisibilityFormDefaultValues'
import { useInvoiceEditorForms } from './invoiceEditorFormsContext'

interface ContextState {
  invoiceLinesColumnsVisibilityForm: UseFormReturn<InvoiceColumnVisibilityFormSchema>
  isLoading: boolean
  isFormDataLoaded: boolean
  submitForm: () => void
}

const InvoiceColumnVisibilityFormContext = createContext<ContextState | undefined>(undefined)

interface InvoiceColumnVisibilityFormContextProviderProps {
  children?: ReactNode
}

export const InvoiceColumnVisibilityFormContextProvider = ({
  children,
}: InvoiceColumnVisibilityFormContextProviderProps): ReactElement => {
  const { isLoading, getDocumentColumns } = useGetZervantDocumentSettings(InvoiceDocumentType.Invoice)
  const { updateInvoiceColumnsVisibilitySettings } = useUpdateInvoiceColumnsVisibilitySettings()
  const initialColumnDataLoaded = useRef(false)

  const { invoiceLinesColumnsVisibilityForm } = useInvoiceEditorForms()

  const { formState, handleSubmit, reset } = invoiceLinesColumnsVisibilityForm
  const isFormDataLoaded = initialColumnDataLoaded.current

  const handleUpdateInvoiceColumnsVisibilitySettings = useCallback(
    (formData: InvoiceColumnVisibilityForm) => {
      if (formState.isDirty) {
        updateInvoiceColumnsVisibilitySettings(formData.selectedColumns)
        // we're resetting the form in order to e.g. reset the isDirty flag, but we don't want
        // the values to be reset to the form's default ones (and keep the ones that were just saved)
        reset({}, { keepValues: true })
      }
    },
    [formState.isDirty, reset, updateInvoiceColumnsVisibilitySettings],
  )

  const submitForm = useCallback(() => {
    handleSubmit(handleUpdateInvoiceColumnsVisibilitySettings)()
  }, [handleSubmit, handleUpdateInvoiceColumnsVisibilitySettings])

  useEffect(() => {
    if (!initialColumnDataLoaded.current && !isLoading) {
      const visibleColumns = getDocumentColumns()
      reset(getInvoiceColumnVisibilityFormDefaultValues(visibleColumns))
      initialColumnDataLoaded.current = true
    }
  }, [getDocumentColumns, isLoading, reset])

  const contextValue = useMemo(
    () => ({
      invoiceLinesColumnsVisibilityForm,
      isFormDataLoaded,
      isLoading,
      submitForm,
    }),
    [invoiceLinesColumnsVisibilityForm, isFormDataLoaded, isLoading, submitForm],
  )

  return (
    <InvoiceColumnVisibilityFormContext.Provider value={contextValue}>
      {children}
    </InvoiceColumnVisibilityFormContext.Provider>
  )
}

export const useInvoiceColumnVisibilitySettingsForm = () => {
  const context = useContext(InvoiceColumnVisibilityFormContext)

  if (!context) {
    throw new Error('InvoiceColumnVisibilityFormContext is missing in the module!')
  }

  return context
}
